import isElement from 'lodash/isElement';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isPlainObject from 'lodash/isPlainObject';

import { findDevice } from '../../helpers/browser';
import createImage from '../../helpers/createImage';
import dom from '../../wrapper/DomWrapper';

import {
  EFFECTS,
  PARALLAX_MULTIPLIER,
  PARALLAX_UPDATE_ACTION,
} from './constants';
import {
  checkEffectByName,
  checkElementVisibility,
  getEffectByName,
} from './utils';

class Parallax {
  constructor(elSection) {
    if (!isElement(elSection)) return;

    const elImage = dom.getElement('.section_bg-img', elSection);

    if (!isElement(elImage)) return;

    this.data = {};
    this.imageData = {};
    this.effects = {};
    this.elSection = elSection;
    this.elImage = elImage;
    this.imageMinHeight = null;
    this.imageTranslate = { y: 0 };
    this.imageIsLoaded = false;
    this.currentDevice = null;
    this.sizes = [];
    this.sources = {};

    this.init();
  }

  static get scrollTop() {
    return dom.window.pageYOffset || dom.document.documentElement.scrollTop;
  }

  static get windowHeight() {
    return dom.window.innerHeight;
  }

  static getPreparedEffects(effects) {
    const parallax = checkEffectByName(effects, EFFECTS.PARALLAX);
    const fixed = checkEffectByName(effects, EFFECTS.FIXED);
    const active = getEffectByName(effects);

    return {
      active,
      parallax,
      fixed,
    };
  }

  static forceUpdate() {
    const customResizeEvent = new Event('resize');

    customResizeEvent.isParallaxForceUpdate = true;

    dom.window.dispatchEvent(customResizeEvent);
  }

  getSource = (device = this.currentDevice) => this.sources[device];

  setSource(device, source) {
    this.sources[device] = source;
  }

  async init() {
    const { effects } = this.elSection.dataset;
    const { sizes } = this.elImage.dataset;

    if (!isNil(effects)) {
      this.effects = this.constructor.getPreparedEffects(JSON.parse(effects));
    }

    if (!isNil(sizes)) {
      this.sizes = JSON.parse(sizes);
    }

    await this.load();
  }

  async load({ isForceUpdate = false } = {}) {
    const newDevice = findDevice(dom.window.innerWidth, this.sizes);

    if (!isForceUpdate && newDevice === this.currentDevice) return;

    this.currentDevice = newDevice;
    await this.updateImageData();
    this.applyImageEffect({ action: PARALLAX_UPDATE_ACTION });
  }

  async loadSource(device = this.currentDevice) {
    const source = {};

    try {
      const { srcset } = dom.getElement(`source[data-device="${device}"]`, this.elImage)
      || dom.getElement('source', this.elImage)
      || {};
      const { naturalWidth, naturalHeight } = await createImage(srcset);

      source.src = srcset;
      source.naturalWidth = naturalWidth;
      source.naturalHeight = naturalHeight;

      this.setSource(device, source);
    } catch (error) {
      console.error(error);
    }

    return source;
  }

  async updateImageData() {
    this.imageIsLoaded = false;

    const { naturalWidth, naturalHeight } = this.getSource() || await this.loadSource();

    this.imageData = {
      naturalWidth,
      naturalHeight,
    };
    this.imageIsLoaded = true;
  }

  updateData() {
    const { scrollTop, windowHeight } = this.constructor;
    const sectionBound = this.elSection.getBoundingClientRect();
    const isVisible = checkElementVisibility({
      scrollTop,
      windowHeight,
      elementBound: sectionBound,
    });

    this.data = {
      isVisible,
      scrollTop,
      windowHeight,
      sectionBound,
    };

    return this.data;
  }

  updateImageMinHeight({ action } = {}) {
    const imageMinHeight = this.getActualImageMinHeight({
      action,
    });

    if (imageMinHeight === this.imageMinHeight) return null;

    dom.updateStyle(this.elImage, {
      minHeight: `${imageMinHeight}px`,
    });

    this.imageMinHeight = imageMinHeight;

    return this.imageMinHeight;
  }

  updateImageTranslate() {
    const imageTranslate = this.getImageTranslate();

    if (
      !isPlainObject(imageTranslate)
      || isEqual(imageTranslate, this.imageTranslate)
    ) return null;

    const { y } = imageTranslate;
    const transform = `translate3D(-50%, ${y}px, 0)`;

    dom.updateStyle(this.elImage, {
      transform,
    });

    this.imageTranslate = {
      ...(isPlainObject(this.imageTranslate) && this.imageTranslate),
      ...imageTranslate,
    };

    return this.imageTranslate;
  }

  getActualImageMinHeight({ action } = {}) {
    let { imageMinHeight } = this;

    if (action !== PARALLAX_UPDATE_ACTION) return imageMinHeight;

    const { data, effects } = this;
    const {
      windowHeight,
      sectionBound: { height: sectionHeight = 0 } = {},
    } = data;
    const { parallax, fixed } = effects;

    if (parallax) {
      imageMinHeight = sectionHeight * 2;
    } else if (fixed) {
      imageMinHeight = windowHeight + sectionHeight;
    }

    return Math.ceil(imageMinHeight);
  }

  getImageTranslate() {
    const { parallax } = this.effects;

    return parallax ? this.getImageParallaxTranslate() : null;
  }

  getImageParallaxTranslate() {
    const {
      windowHeight,
      sectionBound,
    } = this.data || {};
    const { imageData, imageMinHeight: prevImageMinHeight } = this;
    const imageRatio = imageData.naturalWidth / imageData.naturalHeight;
    const imageWidth = Math.max(
      imageData.naturalWidth,
      sectionBound.width
    );
    const imageHeight = Math.max(
      prevImageMinHeight,
      imageWidth / imageRatio
    );
    const maxTranslateY = imageHeight - sectionBound.height;
    const sectionAbsoluteTop = windowHeight - sectionBound.top;
    const translateY = ((sectionAbsoluteTop * maxTranslateY) / windowHeight)
      * PARALLAX_MULTIPLIER;

    return {
      y: translateY > maxTranslateY ? maxTranslateY : translateY,
    };
  }

  applyImageEffect({ action } = {}) {
    const { imageIsLoaded, effects: { active } = {} } = this;

    if (!imageIsLoaded || !active) return;

    const data = this.updateData();

    this.updateImageMinHeight({ action });

    if (!data.isVisible) return;

    this.updateImageTranslate();
  }

  handleScroll() {
    this.applyImageEffect();
  }

  async handleResize(event) {
    const isForceUpdate = !!event?.isParallaxForceUpdate;

    await this.load({ isForceUpdate });
  }
}

export default Parallax;
